import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button, Pagination, Select, SelectItem, Tabs, Tab, TabList, TabPanels, TabPanel, Tag, TextInput } from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import { api } from '../../api/client';
import { BlockSkeleton, EmptyState, ErrorState, PageHeader } from '../../components/states';
import { ExportButton } from '../../components/Filters';
import { GRAIN_LABEL, GrainSwitch, PARTIAL_NOTE, PERIOD_WORD, Panel, periodCell, periodDefaultFrom, type Grain } from '../../components/Analytics';
import { Money, When } from '../../components/format';
import { REGION } from '../../components/LodeChange';
import { pctOf, todayVn } from './common';

function useParam(k: string, def: string) {
  const [params, setParams] = useSearchParams();
  const v = params.get(k) ?? def;
  const set = (x: string) => setParams((p) => { const o = new URLSearchParams(p); if (x && x !== def) o.set(k, x); else o.delete(k); return o; });
  return [v, set] as const;
}

// ---------------------------------------------------------------- open-ticket liability per number
type Liability = {
  date: string; isoDate: string; cityId: number | null; stations: { id: number; name: string; region: string }[];
  totals: { tickets: number; openStake: number; worstNumber: number };
  numbers: { cityId: number; city: string; code: string; type: string; number: string; tickets: number; players: number; stake: number; payoutPerHit: number; cap: number | null; blocked: boolean }[];
  sets: { cityId: number; city: string; code: string; type: string; numbers: string[]; tickets: number; players: number; stake: number; payout: number }[];
};

export function LodeLiabilityPage() {
  const [date, setDate] = useParam('date', todayVn());
  const [city, setCity] = useParam('cityId', '');
  const q = useQuery({
    queryKey: ['lode-liability', date, city],
    queryFn: () => api.get<Liability>('/api/admin/lode/liability', { date, ...(city ? { cityId: city } : {}) }),
    placeholderData: keepPreviousData, refetchInterval: 30_000,
  });
  const d = q.data;
  return (
    <div className="page">
      <PageHeader title="Rủi ro theo số"
        lead="Vé đang chờ kết quả của một ngày quay, gộp theo đài, loại cược và số: tiền đã cược và số tiền phải trả nếu số đó về một lần. Số bị chặn và trần tiền một số lấy từ trang Cấu hình lô đề." />
      <div className="toolbar-filters" role="group" aria-label="Chọn ngày và đài">
        <div><TextInput id="lia-date" type="date" labelText="Ngày quay" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div>
          <Select id="lia-city" labelText="Đài" value={city} onChange={(e) => setCity(e.target.value)}>
            <SelectItem value="" text="Mọi đài quay hôm đó" />
            {(d?.stations ?? []).map((s) => <SelectItem key={s.id} value={String(s.id)} text={`${s.name} (${REGION[s.region]})`} />)}
          </Select>
        </div>
        <span className="toolbar-filters__grow" />
        <Button kind="ghost" size="md" renderIcon={Renew} onClick={() => q.refetch()} disabled={q.isFetching}>Tải lại</Button>
      </div>
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error || !d ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : <>
        <div className="kpis">
          <div className="kpi"><span className="kpi__label">Vé đang chờ</span><strong className="num">{d.totals.tickets}</strong></div>
          <div className="kpi"><span className="kpi__label">Tiền cược đang chờ</span><strong><Money v={d.totals.openStake} unit /></strong></div>
          <div className="kpi"><span className="kpi__label">Số rủi ro nhất: trả nếu về một lần</span><strong><Money v={d.totals.worstNumber} unit /></strong></div>
        </div>
        <div className="section">
          <h2>Theo số</h2>
          {d.numbers.length ? (
            <table className="plain">
              <thead><tr><th>Số</th><th>Đài</th><th>Loại</th><th className="r">Vé</th><th className="r">Người</th><th className="r">Tiền cược tính trần</th><th className="r">Trả nếu về một lần</th><th>Giới hạn</th></tr></thead>
              <tbody>{d.numbers.map((n) => (
                <tr key={`${n.cityId}|${n.code}|${n.number}`}>
                  <td className="is-title num">{n.number}</td><td data-label="Đài">{n.city}</td><td data-label="Loại">{n.type}</td>
                  <td className="r num" data-label="Vé">{n.tickets}</td><td className="r num" data-label="Người">{n.players}</td>
                  <td className="r" data-label="Tiền cược tính trần"><Money v={n.stake} />{n.cap ? <div className="muted">trần <Money v={n.cap} /></div> : null}</td>
                  <td className="r" data-label="Trả nếu về một lần"><Money v={n.payoutPerHit} /></td>
                  <td data-label="Giới hạn">{n.blocked ? <Tag type="red" size="sm">Đang chặn</Tag> : n.cap && n.stake >= n.cap ? <Tag type="magenta" size="sm">Đã chạm trần</Tag> : null}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <EmptyState title="Không có vé chờ" body="Không có vé chờ kết quả cho ngày và đài đã chọn." />}
        </div>
        {d.sets.length ? (
          <div className="section">
            <h2>Cược xiên, trượt (theo bộ số)</h2>
            <table className="plain">
              <thead><tr><th>Bộ số</th><th>Đài</th><th>Loại</th><th className="r">Vé</th><th className="r">Tiền cược</th><th className="r">Trả nếu trúng</th></tr></thead>
              <tbody>{d.sets.map((s) => (
                <tr key={`${s.cityId}|${s.code}|${s.numbers.join(',')}`}>
                  <td className="is-title num">{s.numbers.join(' ')}</td><td data-label="Đài">{s.city}</td><td data-label="Loại">{s.type}</td>
                  <td className="r num" data-label="Vé">{s.tickets}</td><td className="r" data-label="Tiền cược"><Money v={s.stake} /></td><td className="r" data-label="Trả nếu trúng"><Money v={s.payout} /></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : null}
      </>}
    </div>
  );
}

// ---------------------------------------------------------------- stats per station and bet type
type Line = { tickets: number; stake: number; refunded: number; open: number; payout: number; winners: number; settledStake: number; ggr: number; hold: number | null };
type PeriodLine = Line & { day: string; players: number; partial: boolean };
type Stats = {
  from: string; to: string; isoFrom: string; isoTo: string; grain: Grain; players: number; total: Line;
  periods: PeriodLine[];
  stations: (Line & { cityId: number; name: string; region: string | null })[];
  types: (Line & { region: string; code: string; name: string })[];
};

function LineCells({ l }: { l: Line }) {
  return <>
    <td className="r num" data-label="Vé">{l.tickets}</td>
    <td className="r" data-label="Tiền cược"><Money v={l.stake} /></td>
    <td className="r" data-label="Đang chờ"><Money v={l.open} /></td>
    <td className="r" data-label="Đã trả"><Money v={l.payout} /></td>
    <td className="r" data-label="Lãi nhà cái"><Money v={l.ggr} signed /></td>
    <td className="r num" data-label="Tỷ lệ giữ">{pctOf(l.hold)}</td>
    <td className="r" data-label="Đã hoàn"><Money v={l.refunded} /></td>
  </>;
}
const LINE_HEAD = <><th className="r">Vé</th><th className="r">Tiền cược</th><th className="r">Đang chờ</th><th className="r">Đã trả</th><th className="r">Lãi nhà cái</th><th className="r">Tỷ lệ giữ</th><th className="r">Đã hoàn</th></>;

export function LodeStatsPage() {
  const [params, setParams] = useSearchParams();
  const to0 = todayVn();
  const grainParam = params.get('grain');
  const grain = (grainParam === 'week' || grainParam === 'month' ? grainParam : 'day') as Grain;
  const from0 = grain === 'day' ? new Date(Date.parse(to0) - 6 * 86_400_000).toISOString().slice(0, 10) : periodDefaultFrom(to0, grain);
  const from = params.get('from') ?? from0;
  const to = params.get('to') ?? to0;
  const q = useQuery({ queryKey: ['lode-stats', from, to, grain], queryFn: () => api.get<Stats>('/api/admin/lode/stats', { from, to, grain }), placeholderData: keepPreviousData });
  const d = q.data;
  const word = PERIOD_WORD[grain];
  const setFrom = (v: string) => {
    setParams((p) => {
      const o = new URLSearchParams(p);
      if (v) o.set('from', v); else o.delete('from');
      return o;
    });
  };
  const setTo = (v: string) => {
    setParams((p) => {
      const o = new URLSearchParams(p);
      if (v) o.set('to', v); else o.delete('to');
      return o;
    });
  };
  const pickGrain = (g: Grain) => {
    setParams((p) => {
      const o = new URLSearchParams(p);
      if (g && g !== 'day') o.set('grain', g); else o.delete('grain');
      const newFrom = g === 'day' ? new Date(Date.parse(to) - 6 * 86_400_000).toISOString().slice(0, 10) : periodDefaultFrom(to, g);
      o.set('from', newFrom);
      o.set('to', to);
      return o;
    });
  };
  return (
    <div className="page">
      <PageHeader title="Thống kê lô đề"
        lead="Theo ngày quay, tính trên vé lô đề 3 miền. Lãi nhà cái = tiền cược đã chốt trừ tiền đã trả (gồm cả tiền trả sau khi chủ sở hữu sửa kết quả); vé đã hoàn không tính vào tiền cược." />
      <div className="toolbar-filters" role="group" aria-label="Khoảng ngày">
        <GrainSwitch value={grain} onChange={pickGrain} />
        <div><TextInput id="st-from" type="date" labelText="Từ ngày quay" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><TextInput id="st-to" type="date" labelText="Đến ngày quay" value={to} onChange={(e) => setTo(e.target.value)} /></div>
      </div>
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error || !d ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : <>
        <div className="kpis">
          <div className="kpi"><span className="kpi__label">Tiền cược</span><strong><Money v={d.total.stake} unit /></strong></div>
          <div className="kpi"><span className="kpi__label">Đã trả</span><strong><Money v={d.total.payout} unit /></strong></div>
          <div className="kpi"><span className="kpi__label">Lãi nhà cái</span><strong><Money v={d.total.ggr} signed unit /></strong><span className="kpi__hint">giữ {pctOf(d.total.hold)}</span></div>
          <div className="kpi"><span className="kpi__label">Người chơi</span><strong className="num">{d.players}</strong><span className="kpi__hint">{d.total.tickets} vé</span></div>
        </div>
        <Panel title={`Theo ${word}`} aside={<ExportButton path="/api/admin/lode/stats" query={{ from, to, grain, section: 'periods' }} label={`Xuất CSV theo ${word}`} />}>
          {d.periods?.length ? (
            <div className="table-scroll">
              <table className="plain">
                <thead><tr><th>{GRAIN_LABEL[grain]}</th>{LINE_HEAD}<th className="r">Người chơi</th></tr></thead>
                <tbody>{d.periods.map((p) => (
                  <tr key={p.day}>
                    <td className="is-title num">{periodCell(p.day, grain, p.partial)}</td>
                    <LineCells l={p} />
                    <td className="r num" data-label="Người chơi">{p.players}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <EmptyState title="Chưa có vé" body="Không có vé nào trong khoảng ngày này." />}
          <p className="muted note">Người chơi = số tài khoản có đặt cược trong {word} đó, mỗi người đếm một lần.{grain !== 'day' ? ' ' + PARTIAL_NOTE : ''}</p>
        </Panel>
        <Panel title="Theo đài" aside={<ExportButton path="/api/admin/lode/stats" query={{ from, to, grain, section: 'stations' }} label="Xuất CSV đài" />}>
          {d.stations.length ? (
            <div className="table-scroll">
              <table className="plain"><thead><tr><th>Đài</th>{LINE_HEAD}</tr></thead>
                <tbody>{d.stations.map((s) => <tr key={s.cityId}><td className="is-title">{s.name} <span className="muted">{s.region ? REGION[s.region] : ''}</span></td><LineCells l={s} /></tr>)}</tbody>
              </table>
            </div>
          ) : <EmptyState title="Chưa có vé" body="Không có vé nào trong khoảng ngày này." />}
        </Panel>
        {d.types.length ? (
          <Panel title="Theo loại cược" aside={<ExportButton path="/api/admin/lode/stats" query={{ from, to, grain, section: 'types' }} label="Xuất CSV loại cược" />}>
            <div className="table-scroll">
              <table className="plain"><thead><tr><th>Loại</th>{LINE_HEAD}</tr></thead>
                <tbody>{d.types.map((t) => <tr key={t.region + t.code}><td className="is-title">{t.name} <span className="muted">{REGION[t.region] ?? t.region}</span></td><LineCells l={t} /></tr>)}</tbody>
              </table>
            </div>
          </Panel>
        ) : null}
      </>}
    </div>
  );
}

// ---------------------------------------------------------------- Keno and Sieu toc rounds
type Round = { round: number; code: string; numbers?: number[]; sum?: number; result?: string; special?: string; de?: string; forced: boolean; drawnAt: string;
  tickets: number; open: number; players: number; stake: number; payout: number; ggr: number };
type Rounds = { items: Round[]; total: number; page: number; pageSize: number; channels?: { id: number; name: string; chuKy: number }[]; channel?: number };

function RoundTable({ game }: { game: 'keno' | 'st' }) {
  const [page, setPage] = useParam(game + 'Page', '1');
  const [channel, setChannel] = useParam('channel', '');
  const q = useQuery({
    queryKey: ['lode-rounds', game, page, channel],
    queryFn: () => api.get<Rounds>('/api/admin/lode/rounds', { game, page, pageSize: 50, ...(game === 'st' && channel ? { channel } : {}) }),
    placeholderData: keepPreviousData, refetchInterval: 30_000,
  });
  const d = q.data;
  return (
    <div className="stack">
      {game === 'st' && d?.channels ? (
        <div className="toolbar-filters">
          <div><Select id="st-channel" labelText="Kênh" value={String(d.channel ?? '')} onChange={(e) => { setChannel(e.target.value); setPage('1'); }}>
            {d.channels.map((k) => <SelectItem key={k.id} value={String(k.id)} text={`${k.name} (${k.chuKy} giây)`} />)}
          </Select></div>
        </div>
      ) : null}
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error || !d ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !d.items.length ? (
        <EmptyState title="Chưa có kỳ nào" />
      ) : <>
        <div className="table-scroll">
          <table className="plain">
            <thead><tr><th>Kỳ</th><th>Quay lúc</th><th>Kết quả</th><th className="r">Vé</th><th className="r">Chờ</th><th className="r">Người</th><th className="r">Tiền cược</th><th className="r">Đã trả</th><th className="r">Lãi nhà cái</th></tr></thead>
            <tbody>{d.items.map((r) => (
              <tr key={r.round}>
                <td className="is-title num">{r.code}{r.forced ? <> <Tag type="purple" size="sm">Đặt kết quả</Tag></> : null}</td>
                <td data-label="Quay lúc"><When v={r.drawnAt} /></td>
                <td data-label="Kết quả" className="num">{game === 'keno' ? <>{r.numbers?.join(' ')} <span className="muted">tổng {r.sum}, {r.result}</span></> : <>{r.special} <span className="muted">đề {r.de}</span></>}</td>
                <td className="r num" data-label="Vé">{r.tickets}</td><td className="r num" data-label="Chờ">{r.open}</td><td className="r num" data-label="Người">{r.players}</td>
                <td className="r" data-label="Tiền cược"><Money v={r.stake} /></td><td className="r" data-label="Đã trả"><Money v={r.payout} /></td>
                <td className="r" data-label="Lãi nhà cái"><Money v={r.ggr} signed /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <Pagination page={Number(page)} pageSize={50} pageSizes={[50]} totalItems={d.total} itemsPerPageText="Mỗi trang"
          pageRangeText={(_c, t) => `trên ${t} trang`} itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} kỳ`}
          onChange={({ page: p }) => setPage(String(p))} />
      </>}
    </div>
  );
}

export function LodeRoundsPage() {
  const [tab, setTab] = useParam('game', 'keno');
  return (
    <div className="page">
      <PageHeader title="Kỳ Keno và Siêu tốc" lead="Các kỳ đã quay, mới nhất trước, với số vé, tiền cược và tiền trả của mỗi kỳ. Kỳ có thẻ Đặt kết quả là kỳ chủ sở hữu đã đặt trước." />
      <Tabs selectedIndex={tab === 'st' ? 1 : 0} onChange={({ selectedIndex }) => setTab(selectedIndex === 1 ? 'st' : 'keno')}>
        <TabList aria-label="Trò">
          <Tab>Keno</Tab>
          <Tab>Lô đề siêu tốc</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>{tab !== 'st' ? <RoundTable game="keno" /> : null}</TabPanel>
          <TabPanel>{tab === 'st' ? <RoundTable game="st" /> : null}</TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
}
